import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@/main/LoadingSpinner.molecule';
import { faChevronDown } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Badge, ButtonWithDropdown, Icon } from '@seeqdev/qomponents';
import type { BadgeVariant } from '@seeqdev/qomponents';

/** Opens the list of approval steps when the status badge is clicked. */
const ViewStepsBadge = ({
  variant,
  label,
  testId,
  onClick,
}: {
  variant: BadgeVariant;
  label: string;
  testId: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-haspopup="dialog"
    className="tw:flex tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:hover:underline"
    data-testid={testId}
    onClick={onClick}>
    <Badge variant={variant}>{label}</Badge>
  </button>
);

/** Shows approval progress and the available step actions. */
export const ApprovalFlowHeaderDisplay = ({
  isInProd,
  approved,
  isResolvingProductionStatus,
  pendingStepName,
  canApprove,
  isDialogOpen,
  onApprove,
  onViewSteps,
}: {
  isInProd: boolean;
  approved: boolean | undefined;
  isResolvingProductionStatus: boolean;
  pendingStepName: string | undefined;
  canApprove: boolean;
  isDialogOpen: boolean;
  onApprove: () => void;
  onViewSteps: () => void;
}) => {
  const { t } = useTranslation();

  if (isInProd) {
    return (
      <ViewStepsBadge
        variant="success"
        label={t('APPROVAL_FLOWS.IN_PROD')}
        testId="approvalFlowInProductionBadge"
        onClick={onViewSteps}
      />
    );
  }
  if (approved) {
    if (isResolvingProductionStatus) {
      return <LoadingSpinner />;
    }
    return (
      <ViewStepsBadge
        variant="neutral"
        label={t('APPROVAL_FLOWS.DEPRECATED')}
        testId="approvalFlowDeprecatedBadge"
        onClick={onViewSteps}
      />
    );
  }
  return (
    <ButtonWithDropdown
      label={
        <>
          <span>{pendingStepName}</span>
          <Icon icon={faChevronDown} testId="approvalFlowStepChevron" type="inherit" extraClassNames="tw:pl-0.5" />
        </>
      }
      size="xs"
      testId="approvalFlowStep"
      setFocusOnTriggerOnClose={!isDialogOpen}
      dropdownItems={[
        {
          label: t('APPROVAL_FLOWS.OPTIONS.APPROVE_STEP'),
          // Approving a step requires write access to that step. The API checks this too.
          disabled: !canApprove,
          onClick: onApprove,
        },
        {
          label: t('APPROVAL_FLOWS.OPTIONS.VIEW_STEPS'),
          onClick: onViewSteps,
        },
      ]}
    />
  );
};
